import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import {
    IsHydrating,
    useFeature,
    useToggleState,
    WebLink,
} from '@news-mono/web-common'
import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-router'
import {
    KingsCoronationTitleContainer,
    StyledBackgroundWrap,
    StyledClickthrough,
    StyledClickthroughText,
    StyledCNBCLogo,
    StyledCommonwealthGamesContainer,
    StyledCommonwealthGamesText,
    StyledFeatureBorder,
    StyledFeatureContainer,
    StyledFeatureContainer__Deprecated,
    StyledFeatureHeaderContainer,
    StyledFeatureSectionHeader,
    StyledFeatureSectionHeaderContainer,
    StyledFeatureTitleContainer,
    StyledGradientSectionTitle,
    StyledGradientSectionTitleH1,
    StyledGradientWrap,
    StyledGrandFinalSectionHeader,
    StyledGrandFinalText,
    StyledGrandFinalWebLink,
    StyledIconArrowCircle,
    StyledPollieRaterBanner,
    StyledPollieRaterBannerButton,
    StyledPollieRaterBannerTitle,
    StyledPollieRaterBannerWrap,
    StyledThor,
    StyledTitleHeader,
} from '../../compositions/Thor/Thor.styled'
import { isMobileViewport } from '../../content/Video/utils/is-mobile-viewport'
import { Breadcrumb, LinkProps } from '../../navigation'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { colors } from '../../__styling'
import { ContainerWidth, ThemeMargins } from '../../__styling/settings/metrics'
import { ThemeOverrideTypes } from '../../__styling/themes'

export interface ThorProps {
    mainContent: React.ReactElement<any>
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: boolean | keyof ThemeMargins
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: boolean | keyof ThemeMargins
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    themeOverride?: ThemeOverrideTypes
    hideHeadingLink?: boolean
    // Pollie Rater / Federal Election specific props
    hidePollieRaterBanner?: boolean
    isHeadingH1?: boolean
    // {TODO} Move these from apps/thewest/src/app/routes/home/get-themed-segment.ts and into component-library
    layouts?: '4-card' | '8-card' | '7-card'
    hasBorder?: boolean
    // {TODO} Discuss if this should be retained
    unsetFeatureContainerBackground?: boolean
    hideFeatureHeader?: boolean
}

export const Thor: React.FC<ThorProps> = ({
    mainContent,
    hasBackgroundFill,
    horizontalGutters,
    verticalGutters,
    verticalSpacing,
    containerWidth,
    themeOverride,
    hideHeadingLink,
    hidePollieRaterBanner,
    isHeadingH1,
    layouts,
    hasBorder,
    unsetFeatureContainerBackground,
    hideFeatureHeader,
}) => {
    const styledThor = (
        <StyledThor
            hasBackgroundFill={hasBackgroundFill}
            horizontalGutters={horizontalGutters}
            verticalGutters={verticalGutters}
            verticalSpacing={verticalSpacing}
            containerWidth={containerWidth || 1020}
        >
            {mainContent}
        </StyledThor>
    )

    const location = useLocation()
    const isHydrating = useContext(IsHydrating)
    const isMobile = isMobileViewport()

    useEffect(() => {
        if (location.hash && !isHydrating) {
            //hardcoding drop on mobile viewports
            if (isMobile && location.hash === '#cnbc') {
                window.scrollTo({ top: 2100, behavior: 'smooth' })
            } else {
                const yOffset = -20
                const element = document.querySelector(location.hash)
                if (element) {
                    const y =
                        element.getBoundingClientRect().top +
                        window.pageYOffset +
                        yOffset
                    window.scrollTo({ top: y, behavior: 'smooth' })
                }
            }
        }
    }, [location, isHydrating, isMobile])

    const toggles = useToggleState()

    const showPollieRaterBanner =
        useFeature('pollie-rater-drivers') && !hidePollieRaterBanner

    if (themeOverride === 'grand-final') {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledBackgroundWrap themeOverride={themeOverride}>
                    <StyledFeatureContainer__Deprecated
                        initialFlexDirection={'row'}
                        customPadding={16}
                        hasExtraBottomMargin={true}
                        layout={layouts}
                        unsetFeatureContainerBackground={
                            unsetFeatureContainerBackground
                        }
                    >
                        <StyledTitleHeader>
                            <StyledGrandFinalText>
                                AFL Finals 2022
                            </StyledGrandFinalText>
                        </StyledTitleHeader>
                        {!hideHeadingLink && (
                            <div>
                                <StyledGrandFinalWebLink to={'/sport/afl'}>
                                    <StyledGrandFinalSectionHeader>
                                        More Coverage
                                    </StyledGrandFinalSectionHeader>
                                    <StyledIconArrowCircle />
                                </StyledGrandFinalWebLink>
                            </div>
                        )}
                    </StyledFeatureContainer__Deprecated>
                    {styledThor}
                </StyledBackgroundWrap>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'cnbc' || themeOverride === 'cnbcEconomist') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const CNBCLogoSource =
            themeOverride === 'cnbc'
                ? require('../../logos/CNBCLogos/CNBC_Logo_White.png')
                : require('../../logos/CNBCLogos/CNBC_Economist_Logo_White.png')

        return (
            <>
                <a id={'cnbc'} style={{ position: 'absolute' }}></a>
                <ThemeOverrider override={themeOverride}>
                    <StyledFeatureContainer__Deprecated>
                        <WebLink to="/business">
                            <StyledCNBCLogo src={CNBCLogoSource} />
                        </WebLink>
                        <StyledFeatureSectionHeader
                            heading="More stories"
                            headingUrl={
                                themeOverride === 'cnbc'
                                    ? '/business/cnbc'
                                    : '/business#cnbc'
                            }
                            showChevron
                        />
                    </StyledFeatureContainer__Deprecated>
                    {styledThor}
                </ThemeOverrider>
            </>
        )
    }

    if (themeOverride === 'the-economist') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const EconomistLogoSource = require('../../logos/CNBCLogos/Economist_Logo.png')

        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer__Deprecated>
                    <WebLink to="/business">
                        <StyledCNBCLogo src={EconomistLogoSource} />
                    </WebLink>
                    <StyledFeatureSectionHeader
                        heading="More stories"
                        headingUrl="/business/the-economist"
                        showChevron
                    />
                </StyledFeatureContainer__Deprecated>
                {styledThor}
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'best-australian-yarn') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const BestAustralianYarnLogoSource = isFeatureEnabled(
            toFeatureState(toggles),
            'BAY-top-14',
        )
            ? require('../../logos/BestAustralianYarn/CurationLogo2023.png')
            : require('../../logos/BestAustralianYarn/CurationLogo2023.png')

        const BestAustralianYarnTitle = isFeatureEnabled(
            toFeatureState(toggles),
            'BAY-top-14',
        )
            ? '2023 SHORT STORY WINNERS'
            : 'ENTRIES NOW OPEN FOR 2023'

        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    {!hideFeatureHeader && (
                        <StyledFeatureHeaderContainer>
                            <StyledFeatureTitleContainer>
                                <WebLink to="/lifestyle/best-australian-yarn">
                                    <img
                                        src={BestAustralianYarnLogoSource}
                                        height="40"
                                        alt="Top 50 Short Stories"
                                        style={{ display: 'block' }}
                                    />
                                </WebLink>
                                <h2>{BestAustralianYarnTitle}</h2>
                            </StyledFeatureTitleContainer>

                            <StyledClickthrough
                                to={'/lifestyle/best-australian-yarn'}
                            >
                                <StyledClickthroughText fontStyle="sans-serif">
                                    More Coverage
                                </StyledClickthroughText>
                                <StyledIconArrowCircle />
                            </StyledClickthrough>
                        </StyledFeatureHeaderContainer>
                    )}

                    {styledThor}
                </StyledFeatureContainer>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'kings-coronation') {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    {!hideFeatureHeader && (
                        <StyledFeatureHeaderContainer
                            style={{
                                backgroundColor: colors.thewest.coronationRed,
                            }}
                        >
                            <WebLink
                                to="/news/king-charles-iii"
                                style={{ textDecoration: 'none' }}
                            >
                                <KingsCoronationTitleContainer>
                                    THE KING'S CORONATION
                                </KingsCoronationTitleContainer>
                            </WebLink>

                            <StyledFeatureSectionHeader
                                heading="More stories"
                                headingUrl={'/news/king-charles-iii'}
                                showChevron
                            />
                        </StyledFeatureHeaderContainer>
                    )}
                    {styledThor}
                </StyledFeatureContainer>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'commonwealth-games') {
        if (layouts === '4-card') {
            return (
                <ThemeOverrider override={themeOverride}>
                    <StyledFeatureContainer__Deprecated
                        hasExtraBottomMargin={true}
                    >
                        <StyledCommonwealthGamesContainer>
                            <StyledCommonwealthGamesText>
                                COMMONWEALTH &nbsp;
                                <br />
                                GAMES 2022
                            </StyledCommonwealthGamesText>
                        </StyledCommonwealthGamesContainer>
                        <StyledFeatureSectionHeaderContainer>
                            <StyledFeatureSectionHeader
                                heading="Full Coverage"
                                headingUrl="/sport/commonwealth-games"
                                showChevron
                                colorOverride={'#fff'}
                            />
                        </StyledFeatureSectionHeaderContainer>
                    </StyledFeatureContainer__Deprecated>
                    {styledThor}
                </ThemeOverrider>
            )
        } else if (layouts === '7-card') {
            const commonwealthGamesHeaders: LinkProps[] = [
                {
                    text: 'Sport',
                    href: '/sport',
                },
                {
                    text: 'Commonwealth Games',
                    href: '/sport/commonwealth-games',
                },
            ]

            const headingText =
                location.pathname === '/sport' ? (
                    <StyledGradientSectionTitle>
                        Commonwealth Games
                    </StyledGradientSectionTitle>
                ) : (
                    <>
                        <Breadcrumb
                            items={commonwealthGamesHeaders}
                            enableHeaderTag={true}
                            verticalSpacing={'md'}
                        />
                    </>
                )

            return (
                <React.Fragment>
                    <StyledGradientWrap
                        linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                    >
                        <StyledFeatureContainer__Deprecated>
                            {headingText}
                            {!hideHeadingLink && (
                                <StyledFeatureSectionHeader
                                    heading="Full Coverage"
                                    headingUrl="/sport/commonwealth-games"
                                    showChevron
                                    colorOverride={'#fff'}
                                />
                            )}
                        </StyledFeatureContainer__Deprecated>
                        {styledThor}
                    </StyledGradientWrap>
                    {hasBorder && <StyledFeatureBorder />}
                </React.Fragment>
            )
        }
    }

    if (themeOverride === 'federal-election') {
        return (
            <StyledGradientWrap
                linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                boxShadow={true}
            >
                <StyledFeatureContainer__Deprecated>
                    {isHeadingH1 ? (
                        <StyledGradientSectionTitleH1>
                            Federal Election 2022
                        </StyledGradientSectionTitleH1>
                    ) : (
                        <StyledGradientSectionTitle>
                            Federal Election 2022
                        </StyledGradientSectionTitle>
                    )}

                    {!hideHeadingLink && (
                        <StyledFeatureSectionHeader
                            heading="Full Coverage"
                            headingUrl="/politics/federal-election"
                            showChevron={true}
                        />
                    )}
                </StyledFeatureContainer__Deprecated>
                {showPollieRaterBanner && (
                    <StyledPollieRaterBannerWrap>
                        <StyledPollieRaterBanner>
                            <StyledPollieRaterBannerTitle>
                                Rate the politicians running in the election!
                            </StyledPollieRaterBannerTitle>
                            <StyledPollieRaterBannerButton to="/politics/federal-election/pollie-rater">
                                Rate the politicians
                            </StyledPollieRaterBannerButton>
                        </StyledPollieRaterBanner>
                    </StyledPollieRaterBannerWrap>
                )}
                {styledThor}
            </StyledGradientWrap>
        )
    }

    return (
        <ThemeOverrider override={themeOverride}>{styledThor}</ThemeOverrider>
    )
}

Thor.displayName = 'ThorComposition'
